import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import type { PageEstimatePairSource } from '@taptimize/shared/dist/review/estimate-beside-rival';
import type {
  AnswerStructureEvidenceExemplar,
  CompareFactKey,
  CompareSummary,
  CompareSummaryFact,
  CompareSummaryState,
  CompareSummaryTarget,
  DomainTopicUrlRowSnapshot,
  HeadingPathSegment,
  ListInventoryDiff,
  ListInventoryItem,
  PageIntentDiagnosis,
  PageIntentDiagnosisBullet,
  PageIntentDiagnosisBulletKind,
  PageIntentSectionPreview,
  PageSerpMatchType,
  PageSerpQuerySource,
  PageSerpResultRow,
  PageSerpSnapshot,
  RankDrivenSectionGap,
  SectionCoverageGapMemberPath,
  SectionCoverageRow,
  SectionCoverageSiblingLeaf,
  SerpAnswerStructureFamily,
  SerpCollocationHead,
  SerpMixedIntentSample,
  SerpRivalCoverageData,
  SerpRowIntent,
  SerpWinnerExcerpt,
  SupportingPagesByTopicSnapshotData,
  SupportingPagesV2ComparedText,
  SupportingPagesV2EvalData,
  SupportingPagesV2RowResult
} from '@taptimize/shared/dist/review/api-types';
export type {
  AnswerStructureEvidenceExemplar,
  CompareFactKey,
  CompareSummary,
  CompareSummaryFact,
  CompareSummaryState,
  CompareSummaryTarget,
  DomainTopicUrlRowSnapshot,
  HeadingPathSegment,
  ListInventoryDiff,
  ListInventoryItem,
  PageIntentDiagnosis,
  PageIntentDiagnosisBullet,
  PageIntentDiagnosisBulletKind,
  PageIntentSectionPreview,
  PageSerpMatchType,
  PageSerpQuerySource,
  PageSerpResultRow,
  PageSerpSnapshot,
  RankDrivenSectionGap,
  SectionCoverageGapMemberPath,
  SectionCoverageRow,
  SectionCoverageSiblingLeaf,
  SerpAnswerStructureFamily,
  SerpCollocationHead,
  SerpMixedIntentSample,
  SerpRivalCoverageData,
  SerpRowIntent,
  SerpWinnerExcerpt,
  SupportingPagesByTopicSnapshotData,
  SupportingPagesV2ComparedText,
  SupportingPagesV2EvalData,
  SupportingPagesV2RowResult
} from '@taptimize/shared/dist/review/api-types';


export interface PageDemandSideSnapshot {
  label: string;
  source: string;
  confidence: 'high' | 'medium' | 'low' | 'none';
  detail?: string;
}

export interface PageDemandRealizationSnapshot {
  label: string;
  source: string;
  confidence: 'high' | 'medium' | 'low' | 'none';
  position: number | null;
  searchVolume: number | null;
  detail?: string;
}

export interface PageDemandSnapshot {
  claim: PageDemandSideSnapshot | null;
  claimIntentTokens?: string[];
  realization: PageDemandRealizationSnapshot | null;
  ambientTopic: PageDemandSideSnapshot | null;
  splitReasons: string[];
  overrideClaim?: PageDemandOverrideSnapshot | null;
  computedAt: string | null;
}

export interface PageDemandOverrideSnapshot {
  label: string;
  reason: 'wrong_intent' | 'strategist' | 'gsc';
  createdAt: string;
  createdBy: string;
}

export interface PageRealizationCeilingDriverSnapshot {
  keyword: string;
  searchVolume: number;
  matchingTokens: string[];
  upsideAtTargetRank: number;
}

export interface PageRealizationSnapshot {
  realizedVisibility: number;
  realizedSource: 'etv' | 'traffic_proxy';
  demandCeiling: number | null;
  ceiling: number;
  realizationRatio: number | null;
  gap: number | null;
  gapGate: 'scored' | 'low_confidence' | 'pending' | 'unscored';
  ceilingDriver: PageRealizationCeilingDriverSnapshot | null;
  alignedKeywordCount: number;
}

export interface PageHealthSnapshot {
  band: 'critical' | 'weak' | 'ok' | 'best' | 'unscored';
  reasons: string[];
  model: 'realization_gap' | 'eligibility' | 'unscored';
}

export interface SecondaryPageHealthSnapshot {
  band: 'critical' | 'weak' | 'ok' | 'best' | 'unscored' | null;
  model: 'none' | 'freshness_hold' | 'earned_visibility';
  reasons: string[];
  recentlyUpdated: boolean;
  unscoredSegment:
    | 'no_claim'
    | 'low_confidence_claim'
    | 'no_market_data'
    | 'no_aligned_ceiling'
    | 'immaterial_ceiling'
    | null;
}

export interface UrlAnalysisResponse {
  success: boolean;
  data: {
    url: string;
    urlId?: string;
    planClusters?: string[];
    analysis: any | null;
    pageDemand?: PageDemandSnapshot | null;
    pageRealization?: PageRealizationSnapshot | null;
    pageHealth?: PageHealthSnapshot | null;
    pageSecondaryHealth?: SecondaryPageHealthSnapshot | null;
    pageSerp?: PageSerpSnapshot | null;
    pageIntentDiagnosis?: PageIntentDiagnosis | null;
    pageCompare?: CompareSummary | null;
    /** This page's estimate beside the compare target's (TRAFFIC_ESTIMATE_CUTOVER.md D3). */
    pageEstimatePair?: PageEstimatePairSource | null;
    message?: string;
    status: 'completed' | 'pending';
  };
  timestamp: string;
}

export interface RefetchPageAnalysisResponse {
  success: boolean;
  data: {
    url: string;
    status: 'completed' | 'pending';
    taskId: string | null;
    metadata?: {
      dataSource: 'database' | 'api';
      lastUpdated?: string;
      taskId?: string;
    };
  };
  timestamp: string;
}

export interface UrlAnalysisStatusResponse {
  success: boolean;
  data: {
    status: 'completed' | 'pending' | 'failed';
    analysis?: any;
    error?: string;
    metadata?: {
      dataSource?: 'database' | 'api';
      lastUpdated?: string;
    };
  };
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class DataForSEOService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  /**
   * Get the latest PageAnalysis for a specific URL from the database.
   * Calls GET /api/v1/urls/analysis?url=<encoded>.
   *
   * Note: Analyses are created server-side via the page comparison flow
   * (POST /api/v1/urls/compare → DataForSEO pingback → PageAnalysis).
   */
  getUrlAnalysis(
    url: string,
    options?: { refreshAnswerStructure?: boolean }
  ): Observable<UrlAnalysisResponse> {
    const qs = new URLSearchParams();
    qs.set('url', url);
    if (options?.refreshAnswerStructure) {
      qs.set('refreshAnswerStructure', '1');
    }
    return this.http.get<UrlAnalysisResponse>(`${this.API_URL}/urls/analysis?${qs.toString()}`);
  }

  /** Dev-only: force a fresh single-page analysis for this URL. */
  refetchSinglePageAnalysis(url: string): Observable<RefetchPageAnalysisResponse> {
    return this.http.post<RefetchPageAnalysisResponse>(
      `${this.API_URL}/urls/process/refetch-page-analysis`,
      { url }
    );
  }

  /** Poll backend task status for a page-analysis task ID. */
  getUrlAnalysisStatus(taskId: string): Observable<UrlAnalysisStatusResponse> {
    return this.http.get<UrlAnalysisStatusResponse>(`${this.API_URL}/urls/analysis-status/${taskId}`);
  }

  /** Title + H1 from stored PageAnalysis for supporting-page token similarity. */
  getSupportingPageLexical(urls: string[]): Observable<{
    success: boolean;
    data: { byUrl: Record<string, { title: string; h1: string }> };
  }> {
    return this.http.post<{
      success: boolean;
      data: { byUrl: Record<string, { title: string; h1: string }> };
    }>(`${this.API_URL}/urls/supporting-page-lexical`, { urls });
  }

  /** Supporting-page opportunity eval: embedding page-match + content-gap scoring. */
  postSupportingPagesOpportunityEval(body: {
    focalUrl: string;
    competitorUrls: string[];
    yourCandidateUrls: string[];
    focalQuery?: string;
    v1ByCompetitor: Array<{
      competitorUrl: string;
      similarUrl: string | null;
      score: number | null;
      v1CompetitorPreview?: string;
      v1YourMatchPreview?: string | null;
    }>;
  }): Observable<{ success: boolean; data: SupportingPagesV2EvalData }> {
    // Memoised for the session, and the memo is what makes review mode's
    // prefetch work at all.
    //
    // This POST is the slow half of opening a review card — ~4.9s on the pilot,
    // because the SERP-ranked rival is not in the precomputed set (see
    // TECHNICAL_DEBT 0ae). Angular destroys and rebuilds the embed on every
    // step of the belt, so without a memo the same evaluation is re-issued from
    // scratch each time a page is opened, including a page that was just
    // prefetched or one the operator steps back to.
    //
    // `shareReplay` rather than a plain result cache so an in-flight request is
    // shared too: when the operator advances onto a page the prefetch is still
    // evaluating, they join that request instead of starting a second one
    // against a sidecar that is effectively single-threaded (0ad).
    //
    // Safe to memoise for a session: the inputs are stored page analyses, which
    // a crawl changes and a review pass does not. The key includes the
    // competitor set so a different question is a different entry.
    const key = [
      body.focalUrl.trim(),
      [...body.competitorUrls].sort().join(','),
      [...body.yourCandidateUrls].sort().join(',')
    ].join('|');

    const hit = this.opportunityEvalCache.get(key);
    if (hit) return hit;

    const request = this.http
      .post<{ success: boolean; data: SupportingPagesV2EvalData }>(
        `${this.API_URL}/urls/supporting-pages-opportunity-eval`,
        body
      )
      .pipe(
        // A failure must not be remembered as an answer.
        catchError((err) => {
          this.opportunityEvalCache.delete(key);
          return throwError(() => err);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    // Bounded: the belt is at most a few hundred pages and each entry holds one
    // payload, but a long-lived tab should not grow without limit.
    if (this.opportunityEvalCache.size >= 300) this.opportunityEvalCache.clear();
    this.opportunityEvalCache.set(key, request);
    return request;
  }

  /** Session memo for the evaluation above — see the note there. */
  private readonly opportunityEvalCache = new Map<
    string,
    Observable<{ success: boolean; data: SupportingPagesV2EvalData }>
  >();

  getSupportingPagesByTopicSnapshot(url: string): Observable<{
    success: boolean;
    cached: boolean;
    data: SupportingPagesByTopicSnapshotData | null;
  }> {
    return this.http.get<{
      success: boolean;
      cached: boolean;
      data: SupportingPagesByTopicSnapshotData | null;
    }>(`${this.API_URL}/urls/supporting-pages-by-topic-snapshot`, {
      params: { url }
    });
  }

  getSupportingPagesOpportunitySnapshot(url: string): Observable<{
    success: boolean;
    cached: boolean;
    data: SupportingPagesV2EvalData | null;
  }> {
    return this.http.get<{
      success: boolean;
      cached: boolean;
      data: SupportingPagesV2EvalData | null;
    }>(`${this.API_URL}/urls/supporting-pages-opportunity-snapshot`, {
      params: { url }
    });
  }

  /** Precomputed "Top SERP rival" section coverage for a focal URL (see Setup step). */
  getSerpRivalCoverage(url: string): Observable<{
    success: boolean;
    cached: boolean;
    data: SerpRivalCoverageData | null;
  }> {
    return this.http.get<{
      success: boolean;
      cached: boolean;
      data: SerpRivalCoverageData | null;
    }>(`${this.API_URL}/urls/serp-rival-coverage`, {
      params: { url }
    });
  }

  overridePageDemandClaim(input: {
    urlId: string;
    scopeDomain: string;
    overrideClaimLabel: string;
    reason?: 'wrong_intent' | 'strategist' | 'gsc';
    skipSerpCascade?: boolean;
  }): Observable<{
    success: boolean;
    message?: string;
    data?: {
      pageDemand: PageDemandSnapshot;
      pageSerp?: PageSerpSnapshot | null;
      pageIntentDiagnosis?: PageIntentDiagnosis | null;
      serpRefresh?: {
        success: boolean;
        error?: string;
        queryDisplay?: string | null;
        snapshotsWritten?: number;
      };
      serpRivalCoverage?: SerpRivalCoverageData | null;
      serpRivalRefresh?: { success: boolean; error?: string };
    };
  }> {
    return this.http.post<{
      success: boolean;
      message?: string;
      data?: {
        pageDemand: PageDemandSnapshot;
        pageSerp?: PageSerpSnapshot | null;
        pageIntentDiagnosis?: PageIntentDiagnosis | null;
        serpRefresh?: {
          success: boolean;
          error?: string;
          queryDisplay?: string | null;
          snapshotsWritten?: number;
        };
        serpRivalCoverage?: SerpRivalCoverageData | null;
        serpRivalRefresh?: { success: boolean; error?: string };
      };
    }>(`${this.API_URL}/urls/${encodeURIComponent(input.urlId)}/page-demand-override`, {
      scopeDomain: input.scopeDomain,
      overrideClaimLabel: input.overrideClaimLabel,
      reason: input.reason || 'wrong_intent',
      skipSerpCascade: input.skipSerpCascade === true
    });
  }

  refreshSerpRivalCoverage(input: {
    url: string;
    scopeDomain?: string;
  }): Observable<{
    success: boolean;
    data?: {
      serpRivalCoverage: SerpRivalCoverageData | null;
      serpRivalRefresh: { success: boolean; error?: string };
    };
  }> {
    return this.http.post<{
      success: boolean;
      data?: {
        serpRivalCoverage: SerpRivalCoverageData | null;
        serpRivalRefresh: { success: boolean; error?: string };
      };
    }>(`${this.API_URL}/urls/serp-rival-coverage/refresh`, {
      url: input.url,
      scopeDomain: input.scopeDomain
    });
  }

  rebuildSupportingPagesByTopicSnapshot(focalUrl: string): Observable<{
    success: boolean;
    message?: string;
    data: SupportingPagesByTopicSnapshotData | null;
  }> {
    return this.http.post<{
      success: boolean;
      message?: string;
      data: SupportingPagesByTopicSnapshotData | null;
    }>(`${this.API_URL}/urls/supporting-pages-by-topic/rebuild`, { focalUrl });
  }

  rebuildSupportingPagesOpportunitySnapshot(focalUrl: string, options?: { skipV1?: boolean }): Observable<{
    success: boolean;
    message?: string;
    data: {
      supportingPages: SupportingPagesByTopicSnapshotData | null;
      opportunities: SupportingPagesV2EvalData | null;
    };
  }> {
    return this.http.post<{
      success: boolean;
      message?: string;
      data: {
        supportingPages: SupportingPagesByTopicSnapshotData | null;
        opportunities: SupportingPagesV2EvalData | null;
      };
    }>(`${this.API_URL}/urls/supporting-pages-opportunities/rebuild`, {
      focalUrl,
      skipV1: options?.skipV1 === true
    });
  }

} 